import { lazy, Suspense } from "react";
import { Navigation } from "@/components/Navigation";
import { Hero } from "@/components/Hero";
import { ConfidenceBanner } from "@/components/ConfidenceBanner";
import PlatformFeatures from "@/components/PlatformFeatures";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { StickyMobileCTA } from "@/components/StickyMobileCTA";

// Lazy load below-the-fold components for better initial load performance
const ReferralBanner = lazy(() => import("@/components/ReferralBanner").then(m => ({ default: m.ReferralBanner })));
const TrustBanner = lazy(() => import("@/components/TrustBanner").then(m => ({ default: m.TrustBanner })));
const ProfileLinks = lazy(() => import("@/components/ProfileLinks").then(m => ({ default: m.ProfileLinks })));
const WhyEasyTaxes = lazy(() => import("@/components/WhyEasyTaxes").then(m => ({ default: m.WhyEasyTaxes })));
const FiscalSupport = lazy(() => import("@/components/FiscalSupport").then(m => ({ default: m.FiscalSupport })));
const SingleAdvisor = lazy(() => import("@/components/SingleAdvisor").then(m => ({ default: m.SingleAdvisor })));
const GuideFiscalSection = lazy(() => import("@/components/GuideFiscalSection").then(m => ({ default: m.GuideFiscalSection })));
const FAQ = lazy(() => import("@/components/FAQ"));
const FinalCTA = lazy(() => import("@/components/FinalCTA").then(m => ({ default: m.FinalCTA })));

const Index = () => {
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://easy-taxes.ch/#organization",
    "name": "Easy Taxes",
    "url": "https://easy-taxes.ch",
    "logo": {
      "@type": "ImageObject",
      "url": "https://easy-taxes.ch/easy-taxes-logo.png",
      "width": 512,
      "height": 512
    },
    "image": "https://easy-taxes.ch/easy-taxes-og-image.png",
    "description": "Service de déclaration d'impôts en ligne en Suisse avec conseillers fiscaux certifiés",
    "telephone": "+41 78 247 59 24",
    "email": "contact@easy-taxes.ch",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Chemin du Pavillon 2",
      "addressLocality": "Le Grand-Saconnex",
      "postalCode": "1218",
      "addressCountry": "CH"
    },
    "sameAs": [
      "https://g.page/r/CRV6aHqcS3IYEAE",
      "https://fr.trustpilot.com/review/elyx-finance.ch"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+41 78 247 59 24",
      "contactType": "Service client",
      "availableLanguage": ["French"]
    }
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://easy-taxes.ch/#localbusiness",
    "name": "Easy Taxes - ELYX Finance",
    "description": "Service de déclaration d'impôts en Suisse avec conseillers fiscaux dédiés. Plus de 4500 déclarations réalisées avec une note moyenne de 4.9/5.",
    "url": "https://easy-taxes.ch",
    "logo": {
      "@type": "ImageObject",
      "url": "https://easy-taxes.ch/easy-taxes-logo.png",
      "width": 512,
      "height": 512
    },
    "image": "https://easy-taxes.ch/easy-taxes-og-image.png",
    "telephone": "+41 78 247 59 24",
    "email": "contact@easy-taxes.ch",
    "priceRange": "CHF",
    "sameAs": [
      "https://g.page/r/CRV6aHqcS3IYEAE",
      "https://fr.trustpilot.com/review/elyx-finance.ch"
    ],
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Chemin du Pavillon 2",
      "addressLocality": "Le Grand-Saconnex",
      "postalCode": "1218",
      "addressCountry": "CH"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 46.2342,
      "longitude": 6.1159
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "18:00"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Switzerland"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "ratingCount": "47",
      "reviewCount": "47",
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": [
      {
        "@type": "Review",
        "author": { "@type": "Person", "name": "Anaïs P." },
        "datePublished": "2024-11-15",
        "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
        "reviewBody": "Conseillers réactifs et accompagnement parfait pour ma déclaration."
      },
      {
        "@type": "Review",
        "author": { "@type": "Person", "name": "Astrid" },
        "datePublished": "2024-10-22",
        "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
        "reviewBody": "Une équipe sérieuse et toujours à l'écoute pour m'accompagner dans mes choix et mes impôts."
      },
      {
        "@type": "Review",
        "author": { "@type": "Person", "name": "Romarick L." },
        "datePublished": "2024-09-08",
        "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
        "reviewBody": "Service impeccable et professionnel. Je recommande sans hésitation."
      },
      {
        "@type": "Review",
        "author": { "@type": "Person", "name": "Julia D." },
        "datePublished": "2024-12-05",
        "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
        "reviewBody": "Deborah est très professionnelle, à l'écoute et de très bons conseils. On se sent accompagné et en confiance. Je recommande vivement !!"
      },
      {
        "@type": "Review",
        "author": { "@type": "Person", "name": "Manon W." },
        "datePublished": "2025-01-10",
        "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
        "reviewBody": "Un grand merci à l'agence pour son professionnalisme, Déborah et son associé sont à l'écoute et ont répondu à toutes nos questions !"
      },
      {
        "@type": "Review",
        "author": { "@type": "Person", "name": "Julien A." },
        "datePublished": "2025-02-18",
        "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
        "reviewBody": "Bonne équipe de conseil en finance. J'ai été très satisfait de leur aide pour ma déclaration d'impôts."
      }
    ]
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Déclaration d'impôts en Suisse",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Easy Taxes - ELYX Finance"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Switzerland"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Services fiscaux",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Déclaration d'impôts pour résidents suisses",
            "description": "Préparation et optimisation de votre déclaration fiscale avec déductions maximisées"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "DRIS - Demande de Rectification de l'Impôt à la Source",
            "description": "Rectification de l'impôt à la source pour récupérer les déductions auxquelles vous avez droit"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "TOU - Taxation Ordinaire Ultérieure",
            "description": "Passage à l'imposition ordinaire pour optimiser votre situation fiscale"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Déclaration pour frontaliers Suisse-France",
            "description": "Accompagnement fiscal spécialisé pour les travailleurs frontaliers"
          }
        }
      ]
    }
  };

  const videoSchema = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": "Easy Taxes - Comment faire sa déclaration d'impôts en Suisse",
    "description": "Démonstration complète de la plateforme Easy Taxes : découvrez comment faire votre déclaration d'impôts en Suisse avec un conseiller fiscal dédié. Interface intuitive, messagerie directe et suivi centralisé de vos documents fiscaux.",
    "thumbnailUrl": "https://img.youtube.com/vi/aL6OZOf1cF8/maxresdefault.jpg",
    "uploadDate": "2024-01-15",
    "duration": "PT2M30S",
    "contentUrl": "https://www.youtube.com/watch?v=aL6OZOf1cF8",
    "embedUrl": "https://www.youtube-nocookie.com/embed/aL6OZOf1cF8",
    "publisher": {
      "@type": "Organization",
      "name": "Easy Taxes",
      "logo": {
        "@type": "ImageObject",
        "url": "https://easy-taxes.ch/easy-taxes-logo.png",
        "width": 512,
        "height": 512
      }
    },
    "inLanguage": "fr",
    "isFamilyFriendly": true
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Easy Taxes",
    "url": "https://easy-taxes.ch",
    "description": "Service de déclaration d'impôts en Suisse avec conseiller fiscal dédié",
    "inLanguage": "fr",
    "publisher": {
      "@type": "Organization",
      "name": "Easy Taxes",
      "@id": "https://easy-taxes.ch/#organization"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Permis B/G : dois-je faire une déclaration d'impôts en Suisse ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Non, ce n'est pas obligatoire si vous êtes imposé à la source. Cependant, vous pouvez demander une Taxation Ordinaire Ultérieure (TOU) pour faire recalculer votre impôt selon vos déductions réelles (3ᵉ pilier, enfants, frais de santé, etc.). Cette démarche n'est pas toujours avantageuse, car elle peut parfois entraîner un impôt plus élevé. C'est pourquoi Easy Taxes analyse chaque cas individuellement avant de recommander une TOU."
        }
      },
      {
        "@type": "Question",
        "name": "Comment déclarer ses comptes bancaires étrangers en Suisse ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Oui, vous devez déclarer tous vos comptes étrangers. La Suisse exige que tous les comptes, placements et revenus soient déclarés, y compris ceux détenus à l'étranger. Cela ne signifie pas une double imposition : des accords internationaux évitent ce risque. Easy Taxes vous aide à intégrer ces éléments correctement dans votre déclaration fiscale."
        }
      },
      {
        "@type": "Question",
        "name": "Comment corriger sa déclaration d'impôts suisse après envoi ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Oui, c'est possible tant que la taxation définitive n'est pas émise. Vous pouvez demander une rectification ou compléter des justificatifs manquants auprès de l'administration fiscale. Easy Taxes vous accompagne dans cette démarche de correction pour éviter tout oubli ou pénalité."
        }
      },
      {
        "@type": "Question",
        "name": "Erreurs courantes déclaration d'impôts Suisse : comment les éviter ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Les erreurs les plus fréquentes sont : l'oubli de justificatifs de déductions (frais de garde, cotisations 3ᵉ pilier, frais médicaux), la non-déclaration de revenus ou comptes étrangers, des montants mal reportés d'une année à l'autre, et des TOU demandées inutilement entraînant un impôt plus élevé. Easy Taxes vous accompagne pour éviter ces pièges."
        }
      },
      {
        "@type": "Question",
        "name": "Combien coûte une déclaration d'impôts avec Easy Taxes ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "L'accès à la plateforme est gratuit. Nos forfaits démarrent dès 90 CHF pour une déclaration accompagnée par un conseiller fiscal dédié. Un entretien découverte de 30 minutes est offert à chaque inscription. Consultez nos tarifs détaillés sur https://easy-taxes.ch/tarifs."
        }
      }
    ]
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Déclaration d'impôts en Suisse - Conseiller fiscal dédié | Easy Taxes"
        description="Faites votre déclaration d'impôts en Suisse avec un conseiller fiscal dédié. Optimisation des déductions, DRIS, TOU, permis B/C, frontaliers. Espace gratuit →"
        keywords={[
          "déclaration impôts Suisse",
          "impôt à la source",
          "TOU",
          "DRIS",
          "frontalier Suisse",
          "conseiller fiscal Suisse",
          "déclaration fiscale en ligne"
        ]}
        canonical="/"
        schema={{
          "@context": "https://schema.org",
          "@graph": [websiteSchema, organizationSchema, localBusinessSchema, serviceSchema, videoSchema, faqSchema]
        }}
      />
      <Navigation />
      
      {/* Global gradient wrapper for seamless section transitions */}
      <main 
        id="main-content"
        role="main"
        className="relative"
        style={{ 
          background: 'linear-gradient(180deg, #F5F7F4 0%, #E9EFEA 12%, #F2F5F3 28%, #E9EFEA 45%, #F5F7F4 62%, #E9EFEA 80%, #F2F5F3 100%)' 
        }}
      >
        {/* Apple Vision depth orbs - Hidden on mobile for performance, absolute instead of fixed */}
        <div className="hidden md:block absolute inset-0 pointer-events-none overflow-hidden z-0" aria-hidden="true">
          <div 
            className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full"
            style={{ 
              background: 'radial-gradient(circle, rgba(220, 235, 228, 0.4) 0%, transparent 70%)'
            }}
          />
          <div 
            className="absolute top-1/3 -right-60 w-[500px] h-[500px] rounded-full"
            style={{ 
              background: 'radial-gradient(circle, rgba(201, 164, 76, 0.06) 0%, transparent 60%)'
            }}
          />
          <div 
            className="absolute bottom-1/4 -left-40 w-[400px] h-[400px] rounded-full"
            style={{ 
              background: 'radial-gradient(circle, rgba(220, 235, 228, 0.3) 0%, transparent 70%)'
            }}
          />
          <div 
            className="absolute -bottom-60 -right-40 w-[700px] h-[700px] rounded-full"
            style={{ 
              background: 'radial-gradient(circle, rgba(201, 164, 76, 0.08) 0%, transparent 60%)'
            }}
          />
        </div>

        <div className="relative z-10 mobile-scroll-snap">
          <Hero />
          <ConfidenceBanner />
          <div className="content-below-fold">
            <PlatformFeatures />
          </div>
          <Suspense fallback={<div className="h-96" />}>
            <div className="content-below-fold">
              <WhyEasyTaxes />
            </div>
            <div className="content-below-fold">
              <SingleAdvisor />
            </div>
            <div className="content-below-fold">
              <FiscalSupport />
            </div>
            <div className="content-below-fold">
              <ProfileLinks />
              <ReferralBanner />
            </div>
            <div className="content-below-fold">
              <TrustBanner />
            </div>
            <div className="content-below-fold">
              <GuideFiscalSection />
            </div>
            <div className="content-below-fold">
              <FAQ />
            </div>
            <div className="content-below-fold">
              <FinalCTA />
            </div>
          </Suspense>
        </div>
      </main>
      
      <Footer />
      <StickyMobileCTA />
    </div>
  );
};

export default Index;
